import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  getIdrCases, saveIdrOverrides, generateIdr, downloadBlob, draftIdrSummary, reviewIdrCase,
} from "../lib/api";
import { useMsal } from "@azure/msal-react";
import { TrackCheck } from "../components/SharedComponents";
import { getDocuments, getPdfUrl } from "../lib/api";

// Fields the portal cannot source automatically — entered once, then persisted.
const MANUAL_GROUPS = [
  {
    title: "Document",
    fields: [
      { key: "prepared_by", label: "Prepared by (name / organization)" },
    ],
  },
  {
    title: "Provider Qualifications",
    fields: [
      { key: "provider_credentials", label: "Physician credentials / board certification" },
      { key: "tech_credentials", label: "Technologist credentials (e.g. CNIM)" },
    ],
  },
  {
    title: "Claim",
    fields: [
      { key: "payer_name", label: "Payer" },
      { key: "claim_number", label: "Claim number" },
      { key: "cpt_codes", label: "CPT / service codes" },
      { key: "billed_charge", label: "Billed charge" },
      { key: "initial_payment", label: "Initial payment / denial" },
    ],
  },
  {
    title: "Dispute",
    fields: [
      { key: "dispute_number", label: "IDR dispute number" },
      { key: "open_neg_start", label: "Open negotiation start" },
      { key: "open_neg_end", label: "Open negotiation end" },
      { key: "offer_amount", label: "Offer amount" },
    ],
  },
];

// One status per case, combining data readiness with human review. Readiness
// alone read as a finish line ("Ready") even on cases nobody had reviewed yet,
// and sat confusingly beside a separate review column saying otherwise.
//
// Red covers both not-ready shapes but keeps their labels distinct: "Needs
// entry" is a couple of fields to type, "Missing case data" is a real gap, and
// those are very different amounts of work.
const CASE_STATUS = {
  missing:  { color: "#EF4444", label: "Missing case data" },
  entry:    { color: "#EF4444", label: "Needs entry" },
  ready:    { color: "#F59E0B", label: "Ready for review" },
  reviewed: { color: "#10B981", label: "Reviewed" },
};
const CASE_STATUS_ORDER = ["missing", "entry", "ready", "reviewed"];

// Row actions share a width so the column does not jag as labels change
// between states ("Verify ✎" / "View ↗" / "PDF ⬇").
const rowBtn = { minWidth: 92, textAlign: "center" };

// Verify carries the same amber as "Ready for review", so a row that needs
// attention says so twice - in its status and in its action. A reviewed case
// has nothing outstanding, so View stays neutral and recedes.
const verifyBtn = {
  ...rowBtn,
  background: "#FFFBEB",
  border: "1px solid #FDE68A",
  color: "#B45309",
  fontWeight: 600,
};

function caseStatus(c) {
  if (!c) return "missing";
  if (c.reviewStatus === "reviewed") return "reviewed";
  if (c.readiness === "green") return "ready";
  if (c.readiness === "amber") return "entry";
  return "missing";
}

/* The count cards are the status filter; there is no separate control.
 * People do not reliably discover that a card is clickable, so the
 * affordance is both stated above the row and visible on the card: a hover
 * background, and an unmistakable selected state with its own way out. The
 * line also changes to say how to clear, because a filter you cannot find
 * your way out of is worse than no filter.
 */
function StatusCards({ statuses, order, counts, selected, onSelect }) {
  const active = selected.length ? selected[0] : null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <span style={{ fontSize: 10.5, fontStyle: "italic", color: "var(--text-tertiary, #94A3B8)" }}>
          {active
            ? "Showing " + active.toLowerCase() + " only."
            : "Select a card to filter the table by status."}
        </span>
        {active && (
          <button onClick={() => onSelect([])}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
              fontSize: 11.5, color: "#2563EB", textDecoration: "underline" }}>
            Clear filter
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {order.map((k) => {
          const cfg = statuses[k];
          const on = selected.includes(cfg.label);
          return (
            <div key={k} className="card" role="button" tabIndex={0}
              onClick={() => onSelect(on ? [] : [cfg.label])}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(on ? [] : [cfg.label]);
                }
              }}
              onMouseEnter={(e) => {
                if (!on) e.currentTarget.style.background = "#F8FAFC";
              }}
              onMouseLeave={(e) => {
                if (!on) e.currentTarget.style.background = "";
              }}
              title={on ? "Clear this filter" : "Show only " + cfg.label.toLowerCase()}
              style={{ padding: "12px 16px", flex: "1 1 140px", cursor: "pointer",
                transition: "background 0.12s, box-shadow 0.12s",
                borderLeft: "3px solid " + cfg.color,
                boxShadow: on ? "inset 0 0 0 1.5px " + cfg.color : undefined,
                background: on ? "#F8FAFC" : undefined }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontSize: 22, fontWeight: 600, color: cfg.color }}>
                  {counts[k]}
                </span>
                {on && (
                  <span style={{ fontSize: 10, fontFamily: "var(--font-mono)",
                    color: cfg.color }}>filtered</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "#64748B" }}>{cfg.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReadinessDot({ status }) {
  const cfg = CASE_STATUS[status] || CASE_STATUS.missing;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

function SortArrow({ active, dir }) {
  return (
    <span style={{ marginLeft: 4, fontSize: 9, color: active ? "#1B2A4A" : "#CBD5E1" }}>
      {active && dir === "asc" ? "▲" : "▼"}
    </span>
  );
}

function ArbitrationCell({ sent, date }) {
  if (!sent) return <span style={{ fontSize: 11, color: "#94A3B8" }}>-</span>;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#10B981", background: "#ECFDF5",
        border: "1px solid #A7F3D0", padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap" }}>
        ✓ Sent
      </span>
      {date && <span style={{ fontSize: 11, color: "#64748B" }}>{date}</span>}
    </span>
  );
}

function Badge({ kind }) {
  const map = {
    emr: { bg: "#E0F2FE", fg: "#075985", text: "EMR" },
    ai: { bg: "#FEF3C7", fg: "#92400E", text: "AI-extracted" },
    manual: { bg: "#F1F5F9", fg: "#475569", text: "Manual" },
  };
  const c = map[kind] || map.manual;
  return (
    <span style={{
      background: c.bg, color: c.fg, fontSize: 10, fontWeight: 600,
      letterSpacing: "0.03em", padding: "2px 6px", borderRadius: 4,
      textTransform: "uppercase", whiteSpace: "nowrap",
    }}>{c.text}</span>
  );
}

// Quarter presets, mirroring the Audit page.
function quarterOptions(count) {
  const out = [];
  const now = new Date();
  let y = now.getFullYear();
  let q = Math.floor(now.getMonth() / 3) + 1;
  for (let i = 0; i < count; i++) {
    const startMonth = (q - 1) * 3;
    const from = y + "-" + String(startMonth + 1).padStart(2, "0") + "-01";
    const endDate = new Date(y, startMonth + 3, 0);
    const to = y + "-" + String(startMonth + 3).padStart(2, "0") + "-" + String(endDate.getDate()).padStart(2, "0");
    out.push({ label: "Q" + q + " " + y, from, to });
    q -= 1;
    if (q === 0) { q = 4; y -= 1; }
  }
  return out;
}

export default function IDRReporting({ isAdmin }) {
  const { accounts } = useMsal();
  const account = accounts && accounts[0];
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const quarters = useMemo(() => quarterOptions(8), []);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");

  const [selected, setSelected] = useState([]);
  const [verifying, setVerifying] = useState(null);   // case object
  const [draft, setDraft] = useState({});             // manual field values
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [reviewNotes, setReviewNotes] = useState([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [hpUrl, setHpUrl] = useState("");
  const [hpError, setHpError] = useState("");
  const [showHp, setShowHp] = useState(true);
  const narrativeRef = useRef(null);
  const [sort, setSort] = useState({ key: "serviceDate", dir: "desc" });
  const [statusF, setStatusF] = useState([]);   // CASE_STATUS labels, set by the cards
  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(""), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  useEffect(() => {
    return () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  useEffect(() => {
    if (!verifying) { setHpUrl(""); setHpError(""); return; }
    let cancelled = false;
    setHpUrl("");
    setHpError("");
    (async () => {
      try {
        const allDocs = await getDocuments({});
        const hpDoc = (allDocs || []).find((d) =>
          String(d.patientId) === String(verifying.patientId) &&
          (d.sourceType === "History and Physical" ||
          /History[_ ]and[_ ]Physical/i.test(d.filename || ""))
        );
        if (!hpDoc) throw new Error("No H&P document found for this case");
        const r = await getPdfUrl(hpDoc.id);
        if (!r?.url) throw new Error("H&P has no preview URL");
        if (!cancelled) setHpUrl(r.url);
      } catch (e) {
        if (!cancelled) setHpError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [verifying?.patientId]); // eslint-disable-line

  useEffect(() => {
    const el = narrativeRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }, [draft.case_summary, verifying]);

  const preview = async (ids, inlineOverrides) => {
    if (!ids.length) return;
    setGenerating(true);
    setError("");
    setNotice("");
    try {
      const { blob } = await generateIdr(ids, inlineOverrides, "", true);
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(window.URL.createObjectURL(blob));
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const DRAFT_FIELDS = [
    { key: "alerts_documented", label: "Documented alerts / events" },
    { key: "interventions_summary", label: "Alerts & interventions summary" },
  ];

  const markReviewed = async (reviewed) => {
    if (!verifying) return;
    setSaving(true);
    setError("");
    try {
      const who = account?.idTokenClaims?.name || account?.username || "";
      await reviewIdrCase(verifying.patientId, reviewed, who);
      setNotice(reviewed ? "Marked reviewed. Now eligible for bulk download." : "Reopened for editing.");
      const rows = await load();
      const fresh = rows.find((c) => c.patientId === verifying.patientId);
      setVerifying(fresh || { ...verifying, reviewStatus: reviewed ? "reviewed" : "draft" });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runDraft = async () => {
    if (!verifying) return;
    setDrafting(true);
    setError("");
    try {
      const res = await draftIdrSummary(verifying.patientId);
      setDraft({ ...draft, ...res.draft });
      setReviewNotes(res.reviewNotes || []);
      setNotice("Draft generated. Review every line before saving.");
    } catch (e) {
      setError(e.message);
    } finally {
      setDrafting(false);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return getIdrCases(from, to)
      .then((data) => {
        const rows = data.cases || [];
        setCases(rows);
        return rows;
      })
      .catch((e) => { setError(e.message); return []; })
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const visible = cases.filter((c) => {
    if (statusF.length && !statusF.includes(CASE_STATUS[caseStatus(c)].label)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.patientId || "").toLowerCase().includes(q)
      || (c.facility || "").toLowerCase().includes(q)
      || (c.procedure || "").toLowerCase().includes(q)
      || (c.readerName || "").toLowerCase().includes(q);
  });

  const sorted = [...visible].sort((a, b) => {
  const av = a[sort.key] || "";
  const bv = b[sort.key] || "";
  if (av === bv) return 0;
  if (!av) return 1;          // blanks always last
  if (!bv) return -1;
  return sort.dir === "asc" ? (av < bv ? -1 : 1) : (av < bv ? 1 : -1);
});

  const canDownloadCase = (c) =>
    !!c && c.reviewStatus === "reviewed" && c.hasNarrative && c.readiness === "green";
  const greenSelected = selected.filter((id) => {
    const c = cases.find((x) => x.patientId === id);
    return canDownloadCase(c);
  });

  // Counted on the combined status so the cards agree with the column and the
  // filter. Clicking one filters to it, which is the obvious thing to try.
  const counts = CASE_STATUS_ORDER.reduce((acc, k) => {
    acc[k] = cases.filter((c) => caseStatus(c) === k).length;
    return acc;
  }, {});

  const toggle = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : prev.concat([id]));
  };

  const openVerify = (c) => {
    setVerifying(c);
    setDraft(c.overrides || {});
    setReviewNotes(c.reviewNotes || []);
    setNotice("");
  };

  const saveDraft = async () => {
    if (!verifying) return;
    setSaving(true);
    setError("");
    try {
      await saveIdrOverrides(verifying.patientId, draft, "");
      setNotice("Entries saved for case " + verifying.patientId);
      const rows = await load();
      const fresh = rows.find((c) => c.patientId === verifying.patientId);
      if (fresh) setVerifying(fresh);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const generate = async (ids, inlineOverrides) => {
    if (!ids.length) return;
    setGenerating(true);
    setError("");
    setNotice("");
    try {
      const { blob, filename, skipped } = await generateIdr(ids, inlineOverrides, '');
      const outName = ids.length === 1 ? ids[0] + "_IDR.pdf" : (filename || "IDR_batch.zip");
      downloadBlob(blob, outName);
      if (skipped.length) {
        setNotice("Downloaded " + (ids.length - skipped.length) + " document(s). Skipped "
          + skipped.length + " case(s) that still need entries.");
      } else {
        setNotice("Downloaded " + filename);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "#64748B",
    letterSpacing: "0.04em", textTransform: "uppercase", padding: "10px 12px",
    borderBottom: "1px solid #E2E8F0", whiteSpace: "nowrap",
    position: "sticky", top: 0, background: "#fff", zIndex: 1 };
  const td = { padding: "11px 12px", fontSize: 13, borderBottom: "1px solid #F1F5F9",
    color: "var(--text-primary, #1B2A4A)" };
  const input = { width: "100%", padding: "7px 9px", fontSize: 13, borderRadius: 6,
    border: "1px solid #CBD5E1", background: "#fff", color: "#1B2A4A" };

  if (!isAdmin) {
  return (
      <div className="page-body">
        <div className="card" style={{ padding: 32, textAlign: "center" }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Admin access required</div>
            <div style={{ fontSize: 13, color: "#64748B" }}>Ask an administrator to grant access to IDR reporting.</div>
        </div>
      </div>
    );
  }

  return (
  <>
    <div className="page-header">
      <div className="page-title-group">
        <div className="page-title">IDR</div>
        <div className="page-subtitle" style={{ fontSize:12, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)" }}>
          Independent dispute resolution letter preparation
        </div>
      </div>
    </div>

    <div className="page-body">
      {/* Filters */}
      <div className="card" style={{ padding: 16, marginBottom: 16, display: "flex",
        gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ minWidth: 150 }}>
          <label style={{ fontSize: 11, color: "#64748B", display: "block", marginBottom: 4 }}>QUARTER</label>
          <select
            style={input}
            value={from + "|" + to}
            onChange={(e) => {
              const parts = e.target.value.split("|");
              setFrom(parts[0]); setTo(parts[1]);
            }}
          >
            <option value="|">All dates</option>
            {quarters.map((q) => (
              <option key={q.label} value={q.from + "|" + q.to}>{q.label}</option>
            ))}
          </select>
        </div>
        <div style={{ minWidth: 140 }}>
          <label style={{ fontSize: 11, color: "#64748B", display: "block", marginBottom: 4 }}>FROM</label>
          <input type="date" style={input} value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div style={{ minWidth: 140 }}>
          <label style={{ fontSize: 11, color: "#64748B", display: "block", marginBottom: 4 }}>TO</label>
          <input type="date" style={input} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <label style={{ fontSize: 11, color: "#64748B", display: "block", marginBottom: 4 }}>SEARCH</label>
          <input style={input} placeholder="Case, facility, procedure, reader"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <StatusCards statuses={CASE_STATUS} order={CASE_STATUS_ORDER}
        counts={counts} selected={statusF} onSelect={setStatusF} />

      {error && (
        <div className="card" style={{ padding: 12, marginBottom: 12, borderLeft: "3px solid #EF4444", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 12, display: "flex",
          alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13 }}>
            {selected.length} selected · {greenSelected.length} reviewed & ready
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" onClick={() => setSelected([])}>Clear</button>
            <button
              className="btn btn-primary"
              disabled={!greenSelected.length || generating}
              onClick={() => generate(greenSelected)}
            >
              {generating ? "Generating..." : "Download " + greenSelected.length + " as ZIP"}
            </button>
          </div>
        </div>
      )}

      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 6 }}>
        Showing {visible.length} of {cases.length} cases
      </div>

      {/* Case table */}
      <div className="card" style={{ padding: 0, overflowX: "auto", overflowY: "auto", maxHeight: "60vh" }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "#64748B", fontSize: 13 }}>Loading cases...</div>
        ) : !visible.length ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>No cases in this range</div>
            <div style={{ fontSize: 13, color: "#64748B" }}>
              Only cases with a History and Physical appear here. Upload one as
              <span style={{ fontFamily: "var(--font-mono, monospace)" }}> {"{PID}"}_History_and_Physical.pdf</span> to make a case eligible.
            </div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 36 }}></th>
                <th style={{ ...th, width: 150 }}>Status</th>
                <th style={th}>Case</th>
                <th style={{ ...th, width:60, textAlign:"center" }} title="Sent to arbitration">Arb</th>
                <th style={{ ...th, cursor: "pointer" }} onClick={() => toggleSort("sentToArbitrationDate")}>
                  Arb Date <SortArrow active={sort.key === "sentToArbitrationDate"} dir={sort.dir} />
                </th>
                <th style={{ ...th, cursor: "pointer" }} onClick={() => toggleSort("serviceDate")}>
                  Service Date <SortArrow active={sort.key === "serviceDate"} dir={sort.dir} />
                </th>
                <th style={th}>Facility</th>
                <th style={th}>Procedure</th>
                <th style={th}>Reader</th>
                <th style={{ ...th, textAlign: "right" }}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.patientId}>
                  <td style={td}>
                    <input type="checkbox" checked={selected.includes(c.patientId)}
                      onChange={() => toggle(c.patientId)} />
                  </td>
                  <td style={td}><ReadinessDot status={caseStatus(c)} /></td>
                  <td style={{ ...td, fontFamily: "var(--font-mono, monospace)", fontSize: 12.5 }}>{c.patientId}</td>
                  <td style={{ ...td, textAlign:"center" }}><TrackCheck done={c.sentToArbitration} /></td>
                  <td style={td}>{c.sentToArbitrationDate || "-"}</td>
                  <td style={td}>{c.serviceDate || "-"}</td>
                  <td style={td}>{c.facility || "-"}</td>
                  <td style={td}>{c.procedure || "-"}</td>
                  <td style={td}>{c.readerName || "-"}</td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                    {/* A reviewed case is not being verified any more - the panel
                        this opens offers Reopen rather than Mark reviewed, so the
                        button should say what it actually does now. */}
                    <button className="btn" onClick={() => openVerify(c)}
                      style={{ ...(c.reviewStatus === "reviewed" ? rowBtn : verifyBtn), marginRight: 6 }}
                      title={c.reviewStatus === "reviewed"
                        ? "Open this case. Reopen it from inside to make changes."
                        : "Check the case details and mark it reviewed"}>
                      {c.reviewStatus === "reviewed" ? "View ↗" : "Verify ✎"}
                    </button>
                    <button className="btn btn-primary" style={rowBtn}
                      disabled={generating || !canDownloadCase(c)}
                      title={!canDownloadCase(c)
                        ? (c.reviewStatus !== "reviewed" ? "Mark reviewed to enable"
                          : !c.hasNarrative ? "Needs a narrative"
                          : "Missing case data")
                        : ""}
                      onClick={() => generate([c.patientId])}>
                      PDF ⬇
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Verify panel */}
      {verifying && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(15,27,46,0.45)",
            display: "flex", justifyContent: "flex-end", zIndex: 50 }}
          onClick={() => setVerifying(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: showHp ? 1180 : 520, maxWidth: "100%", background: "#fff",
              height: "100%", display: "flex", boxShadow: "-8px 0 32px rgba(0,0,0,0.2)" }}
          >
            {showHp && (
              <div style={{ flex: 1, minWidth: 0, borderRight: "1px solid #E2E8F0",
                display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 14px", borderBottom: "1px solid #F1F5F9" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#64748B",
                    letterSpacing: "0.04em", textTransform: "uppercase" }}>History and Physical</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    {hpUrl && <a href={hpUrl} target="_blank" rel="noopener noreferrer" className="btn">Open ↗</a>}
                    <button className="btn" onClick={() => setShowHp(false)}>Hide</button>
                  </div>
                </div>
                {hpUrl ? (
                  <iframe title="H&P" src={hpUrl} style={{ flex: 1, border: "none" }} />
                ) : (
                  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                    padding: 24, textAlign: "center", fontSize: 13, color: "#64748B" }}>
                    {hpError || "Loading H&P..."}
                  </div>
                )}
              </div>
            )}

            <div style={{ width: 520, flexShrink: 0, overflowY: "auto", padding: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <h2 style={{ fontSize: 18, margin: 0 }}>Case {verifying.patientId}</h2>
                <div style={{ display: "flex", gap: 6 }}>
                  {!showHp && <button className="btn" onClick={() => setShowHp(true)}>Show H&P</button>}
                  <button className="btn" onClick={() => setVerifying(null)}>Close</button>
                </div>
              </div>
              <p style={{ fontSize: 12.5, color: "#64748B", marginTop: 0, marginBottom: 20 }}>
                Review the sourced values, then fill in what the portal cannot supply.
              </p>

              {/* Sourced values, read-only with provenance */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", letterSpacing: "0.04em",
                  textTransform: "uppercase", marginBottom: 10 }}>From the portal</div>
                {[
                  ["Service date", verifying.serviceDate, "emr"],
                  ["Monitoring duration", verifying.monitoringDuration, "emr"],
                  ["Facility", verifying.facility, "emr"],
                  ["Procedure", verifying.procedure, "emr"],
                  ["Modalities", verifying.modalities, verifying.modalitiesSource || "ai"],
                  ["Interpreting physician", verifying.readerName, "emr"],
                  ["Technologist", verifying.techName, "emr"],
                  ["Sent to arbitration", verifying.sentToArbitration ? "Yes" : "No", "emr"],
                  ["Date sent to arbitration", verifying.sentToArbitrationDate, "emr"],
                ].map((row) => (
                  <div key={row[0]} style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
                    <div style={{ fontSize: 12.5, color: "#64748B", flexShrink: 0 }}>{row[0]}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, textAlign: "right" }}>
                      <span style={{ fontSize: 13 }}>{row[1] || "-"}</span>
                      <Badge kind={row[2]} />
                    </div>
                  </div>
                ))}
                <p style={{ fontSize: 11.5, color: "#92400E", background: "#FEF3C7", padding: "8px 10px",
                  borderRadius: 6, marginTop: 12, marginBottom: 0 }}>
                  AI-extracted values come from document analysis. Confirm them against the source report
                  before submitting to a payer.
                </p>
              </div>

              {/* Narrative + complexity — GPT drafts, human approves */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", letterSpacing: "0.04em",
                    textTransform: "uppercase", display: "flex", gap: 8, alignItems: "center" }}>
                    Summary of Services <Badge kind="ai" />
                  </div>
                  <button className="btn" disabled={drafting} onClick={runDraft}>
                    {drafting ? "Drafting..." : "Draft from reports"}
                  </button>
                </div>

                {DRAFT_FIELDS.map((f) => (
                  <div key={f.key} style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>
                      {f.label}
                    </label>
                    <input style={input} value={draft[f.key] || ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />
                  </div>
                ))}

                <label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 4 }}>
                  Narrative
                </label>
                <textarea
                  ref={narrativeRef}
                  style={{ ...input, minHeight: 160, maxHeight: "60vh", overflowY: "auto",
                    resize: "none", fontFamily: "inherit", lineHeight: 1.55 }}
                  value={draft.case_summary || ""}
                  onChange={(e) => setDraft({ ...draft, case_summary: e.target.value })}
                  placeholder="Draft from reports, then edit. Blank until you save."
                />
                <p style={{ fontSize: 11.5, color: "#92400E", background: "#FEF3C7", padding: "8px 10px",
                  borderRadius: 6, marginTop: 8, marginBottom: 0 }}>
                  Drafted by AI from the case documents. Read every sentence against
                  the source before saving — this text goes to a payer as evidence.
                </p>

                {reviewNotes.length > 0 && (
                  <div style={{ marginTop: 10, padding: "10px 12px", background: "#FEF2F2",
                    border: "1px solid #FECACA", borderRadius: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#B91C1C", letterSpacing: "0.04em",
                      textTransform: "uppercase", marginBottom: 6 }}>
                      Omitted for missing information ({reviewNotes.length})
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#7F1D1D", lineHeight: 1.6 }}>
                      {reviewNotes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                    <div style={{ fontSize: 11.5, color: "#7F1D1D", marginTop: 8 }}>
                      These were left out rather than guessed at. Add them manually if the letter needs them.
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: 8, position: "sticky", bottom: 0,
                background: "#fff", paddingTop: 12, borderTop: "1px solid #E2E8F0" }}>
                <button className="btn" disabled={saving} onClick={saveDraft}>
                  {saving ? "Saving..." : "Save entries"}
                </button>
                <button
                  className="btn"
                  disabled={generating}
                  onClick={() => preview([verifying.patientId], draft)}
                >
                  {generating ? "Building..." : "Preview"}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={generating}
                  onClick={() => generate([verifying.patientId], draft)}
                >
                  {generating ? "Generating..." : "Download PDF"}
                </button>
                {verifying.reviewStatus === "reviewed" ? (
                  <button className="btn" disabled={saving} onClick={() => markReviewed(false)}>
                    Reopen ↺
                  </button>
                ) : (
                  <button className="btn btn-primary" disabled={saving} onClick={() => markReviewed(true)}>
                    Mark reviewed ✓
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {previewUrl && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(15,27,46,0.55)", zIndex: 60,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={() => { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }}
        >
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 10, width: "min(900px, 100%)", height: "90vh",
              display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "12px 16px", borderBottom: "1px solid #E2E8F0" }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                Preview{verifying ? " — case " + verifying.patientId : ""}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {verifying && (
                  <button className="btn btn-primary" onClick={() => generate([verifying.patientId], draft)}>
                    Download
                  </button>
                )}
                <button className="btn" onClick={() => { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }}>
                  Close
                </button>
              </div>
            </div>
            <iframe title="IDR preview" src={previewUrl} style={{ flex: 1, border: "none" }} />
          </div>
        </div>
      )}

      {notice && (
        <div
          role="status"
          style={{ position: "fixed", left: "50%", bottom: 24, transform: "translateX(-50%)",
            zIndex: 70, display: "flex", alignItems: "center", gap: 12,
            background: "#0F1B2E", color: "#F8FAFC", fontSize: 13,
            padding: "11px 14px", borderRadius: 8, maxWidth: "min(560px, calc(100vw - 32px))",
            boxShadow: "0 8px 24px rgba(15,27,46,0.28)" }}
        >
          <span style={{ color: "#6EE7B7", flexShrink: 0 }}>✓</span>
          <span style={{ flex: 1 }}>{notice}</span>
          <button
            onClick={() => setNotice("")}
            aria-label="Dismiss"
            style={{ background: "none", border: "none", color: "#94A3B8", cursor: "pointer",
              fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0 }}
          >×</button>
        </div>
      )}
    </div>
    </>
  );
}